/**
 * Step 1 (Yandex): Configuration for Yandex Parsing
 */

import {
  Card,
  Space,
  Spin,
  Checkbox,
  Button,
  Badge,
  Alert,
  Row,
  Col,
  Segmented,
  InputNumber,
  Tag,
  Tooltip,
  Form,
  Select,
  Typography,
  Modal,
} from 'antd'
import {
  RocketOutlined,
  SearchOutlined,
  EnvironmentOutlined,
  ToolOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons'
import type { FormInstance } from 'antd'
import { getRegionName } from '../constants'
import type { ResolvedRegion, QueriesRegionsData } from '../types'

const { Text, Title } = Typography

interface Project {
  id: number
  crm_id: number
  project_name: string
  status: string
}

interface ConfigStepProps {
  // Project info
  projectId?: number | null
  projectName?: string | null

  // Form and projects (for project selector mode)
  form: FormInstance
  projects: Project[]
  loadingProjects: boolean
  onLoadQueriesRegions: (projectId: number) => void
  onStartParsing: (values?: { project_id?: number }) => void

  // Queries and regions
  queriesRegions: QueriesRegionsData | null
  loadingQueriesRegions: boolean
  resolvedRegions: ResolvedRegion[]
  selectedQueries: string[]
  setSelectedQueries: (queries: string[]) => void
  selectedRegionCodes: string[]
  setSelectedRegionCodes: (codes: string[]) => void

  // Parser settings
  downloadAll: boolean
  setDownloadAll: (value: boolean) => void
  minClicks: number
  setMinClicks: (value: number) => void
  topQueries: number
  setTopQueries: (value: number) => void
}

export default function ConfigStep({
  projectId,
  projectName,
  form,
  projects,
  loadingProjects,
  onLoadQueriesRegions,
  onStartParsing,
  queriesRegions,
  loadingQueriesRegions,
  resolvedRegions,
  selectedQueries,
  setSelectedQueries,
  selectedRegionCodes,
  setSelectedRegionCodes,
  downloadAll,
  setDownloadAll,
  minClicks,
  setMinClicks,
  topQueries,
  setTopQueries,
}: ConfigStepProps) {
  // If project is provided, show full config
  if (projectId && projectName) {
    const queries = queriesRegions?.queries || []
    const regionsToShow = resolvedRegions.length > 0
      ? resolvedRegions
      : (queriesRegions?.regions || [])

    const unresolvedRegions = resolvedRegions.filter((r) => !r.success)
    const hasUnresolvedRegions = unresolvedRegions.length > 0

    return (
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Card
          bordered={false}
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white'
          }}
        >
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Title level={4} style={{ margin: 0, color: 'white' }}>
              <RocketOutlined /> Парсинг Yandex
            </Title>
            <Text style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: 16 }}>
              {projectName}
            </Text>
          </Space>
        </Card>

        {loadingQueriesRegions ? (
          <Card>
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin size="large" />
              <div style={{ marginTop: 16 }}>
                <Text type="secondary">Загрузка параметров проекта...</Text>
              </div>
            </div>
          </Card>
        ) : (
          <>
            {/* Queries Card */}
            <Card
              title={
                <Space>
                  <SearchOutlined style={{ color: '#1890ff' }} />
                  <span>Поисковые запросы</span>
                  <Badge count={queries.length} showZero style={{ backgroundColor: '#52c41a' }} />
                  <Badge count={selectedQueries.length} showZero style={{ backgroundColor: '#1890ff' }} title="Выбрано" />
                </Space>
              }
              size="small"
              extra={
                queries.length > 0 && (
                  <Space size="small">
                    <Button
                      size="small"
                      type="link"
                      onClick={() => setSelectedQueries(queries)}
                    >
                      Все
                    </Button>
                    <Button
                      size="small"
                      type="link"
                      onClick={() => setSelectedQueries([])}
                    >
                      Сбросить
                    </Button>
                  </Space>
                )
              }
            >
              {queries.length > 0 ? (
                <Checkbox.Group
                  value={selectedQueries}
                  onChange={(checkedValues) => setSelectedQueries(checkedValues as string[])}
                  style={{ width: '100%' }}
                >
                  <Space size={[8, 8]} wrap style={{ width: '100%' }}>
                    {queries.map((query, index) => (
                      <Checkbox
                        key={index}
                        value={query}
                        style={{
                          border: '1px solid #d9d9d9',
                          borderRadius: 16,
                          padding: '4px 12px',
                          margin: 0,
                          background: selectedQueries.includes(query) ? '#e6f7ff' : '#fff'
                        }}
                      >
                        <Text style={{ fontSize: 13 }}>{query}</Text>
                      </Checkbox>
                    ))}
                  </Space>
                </Checkbox.Group>
              ) : (
                <Text type="secondary">Запросы не найдены</Text>
              )}
            </Card>

            {/* Regions Card */}
            <Card
              title={
                <Space>
                  <EnvironmentOutlined style={{ color: '#52c41a' }} />
                  <span>Регионы</span>
                  <Badge count={regionsToShow.length} showZero style={{ backgroundColor: '#52c41a' }} />
                  <Badge count={selectedRegionCodes.length} showZero style={{ backgroundColor: '#1890ff' }} title="Выбрано" />
                </Space>
              }
              size="small"
              extra={
                regionsToShow.length > 0 && (
                  <Space size="small">
                    <Button
                      size="small"
                      type="link"
                      onClick={() => {
                        const allResolved = regionsToShow
                          .filter((r: any) => r.success && r.yandex_id)
                          .map((r: any) => r.yandex_id.toString())
                        setSelectedRegionCodes(allResolved)
                      }}
                    >
                      Все
                    </Button>
                    <Button
                      size="small"
                      type="link"
                      onClick={() => setSelectedRegionCodes([])}
                    >
                      Сбросить
                    </Button>
                  </Space>
                )
              }
            >
              {regionsToShow.length > 0 ? (
                <Checkbox.Group
                  value={selectedRegionCodes}
                  onChange={(checkedValues) => setSelectedRegionCodes(checkedValues as string[])}
                  style={{ width: '100%' }}
                >
                  <Space size={[8, 8]} wrap style={{ width: '100%' }}>
                    {regionsToShow.map((region: any, index: number) => {
                      const regName = region.original_name || region.name || getRegionName(region.id?.toString() || "")
                      const isResolved = region.success
                      const regionId = region.yandex_id || region.original_id || region.id
                      const regionIdStr = regionId?.toString()

                      return (
                        <Checkbox
                          key={index}
                          value={regionIdStr}
                          disabled={!isResolved}
                          style={{
                            border: '1px solid #d9d9d9',
                            borderRadius: 16,
                            padding: '4px 12px',
                            margin: 0,
                            background: selectedRegionCodes.includes(regionIdStr) ? '#f6ffed' : '#fff',
                            opacity: isResolved ? 1 : 0.6
                          }}
                        >
                          <Text style={{ fontSize: 13 }}>
                            {regName} <span style={{ opacity: 0.7 }}>({regionId})</span>
                            {isResolved && <span style={{ marginLeft: 4, fontSize: 10 }}>✓</span>}
                            {!isResolved && <span style={{ marginLeft: 4, fontSize: 10 }}>⚠</span>}
                          </Text>
                        </Checkbox>
                      )
                    })}
                  </Space>
                </Checkbox.Group>
              ) : (
                <Text type="secondary">Регионы не найдены</Text>
              )}
            </Card>

            {hasUnresolvedRegions && (
              <Alert
                message="Внимание: некоторые регионы не найдены"
                type="warning"
                showIcon
              />
            )}

            {/* Parser Configuration */}
            <Card
              title={
                <Space>
                  <ToolOutlined style={{ color: '#1890ff' }} />
                  <span>Настройки парсера</span>
                </Space>
              }
              size="small"
              style={{ border: '1px solid #e6f7ff', background: '#fafafa' }}
            >
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {/* Row 1: Mode selector and Top-N count */}
                <Row gutter={16} align="middle">
                  <Col span={downloadAll ? 24 : 14}>
                    <div style={{ marginBottom: 4 }}>
                      <Text strong style={{ fontSize: 13 }}>Режим загрузки</Text>
                    </div>
                    <Segmented
                      value={downloadAll}
                      options={[
                        {
                          label: (
                            <Space size={4}>
                              <span>ТОП-N</span>
                              <Tag color="green" style={{ margin: 0, fontSize: 10 }}>рек.</Tag>
                            </Space>
                          ),
                          value: false
                        },
                        {
                          label: (
                            <Space size={4}>
                              <span>Все запросы</span>
                              <Tag color="red" style={{ margin: 0, fontSize: 10 }}>⚠️</Tag>
                            </Space>
                          ),
                          value: true
                        },
                      ]}
                      onChange={(value) => {
                        setDownloadAll(value as boolean)
                        if (value === true) {
                          Modal.warning({
                            title: '⚠️ Экстремальный режим',
                            content: (
                              <div>
                                <p><strong>Это может:</strong></p>
                                <ul style={{ marginTop: 8 }}>
                                  <li>Значительно увеличить время парсинга</li>
                                  <li>Создать высокую нагрузку на Yandex API</li>
                                </ul>
                              </div>
                            ),
                            okText: 'Понятно',
                            width: 400,
                          })
                        }
                      }}
                      style={{ width: '100%' }}
                    />
                  </Col>
                  {!downloadAll && (
                    <Col span={10}>
                      <div style={{ marginBottom: 4 }}>
                        <Text strong style={{ fontSize: 13 }}>
                          Лимит запросов
                          <Tooltip title="Количество запросов для загрузки в режиме ТОП-N">
                            <QuestionCircleOutlined style={{ color: '#1890ff', marginLeft: 6, fontSize: 12 }} />
                          </Tooltip>
                        </Text>
                      </div>
                      <InputNumber
                        value={topQueries}
                        onChange={(value) => setTopQueries(value ?? 100)}
                        min={10}
                        max={1000}
                        step={10}
                        style={{ width: '100%' }}
                        addonAfter="шт"
                      />
                    </Col>
                  )}
                </Row>

                {/* Row 2: Min clicks filter */}
                <Row gutter={16} align="middle">
                  <Col span={14}>
                    <div style={{ marginBottom: 4 }}>
                      <Text strong style={{ fontSize: 13 }}>
                        Минимум кликов
                        <Tooltip title="Запросы с меньшим количеством кликов будут отфильтрованы. 0 = без фильтра">
                          <QuestionCircleOutlined style={{ color: '#1890ff', marginLeft: 6, fontSize: 12 }} />
                        </Tooltip>
                      </Text>
                    </div>
                    <InputNumber
                      value={minClicks}
                      onChange={(value) => setMinClicks(value ?? 5)}
                      min={0}
                      max={100}
                      style={{ width: '100%' }}
                      addonAfter="кликов"
                    />
                  </Col>
                  <Col span={10}>
                    {/* Summary preview */}
                    <div style={{
                      padding: '8px 12px',
                      background: downloadAll ? '#fff1f0' : '#f6ffed',
                      borderRadius: 6,
                      border: `1px solid ${downloadAll ? '#ffccc7' : '#b7eb8f'}`,
                      textAlign: 'center'
                    }}>
                      <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Итого:</Text>
                      <Text strong style={{ fontSize: 13, color: downloadAll ? '#ff4d4f' : '#52c41a' }}>
                        {downloadAll ? 'Все запросы' : `ТОП-${topQueries}`}
                      </Text>
                      <Text style={{ fontSize: 11, display: 'block', color: '#666' }}>
                        {minClicks > 0 ? `≥${minClicks} кл.` : 'без фильтра'}
                      </Text>
                    </div>
                  </Col>
                </Row>

                {/* Summary line */}
                <div style={{
                  padding: '8px 12px',
                  background: '#e6f7ff',
                  borderRadius: 4,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <Text style={{ fontSize: 12 }}>
                    Комбинаций: <Text strong>{selectedQueries.length} × {selectedRegionCodes.length} = {selectedQueries.length * selectedRegionCodes.length}</Text>
                  </Text>
                  <Tag color={downloadAll ? 'red' : 'blue'} style={{ margin: 0 }}>
                    {downloadAll
                      ? `Все запросы${minClicks > 0 ? `, ≥${minClicks} кл.` : ''}`
                      : `ТОП-${topQueries}${minClicks > 0 ? `, ≥${minClicks} кл.` : ''}`
                    }
                  </Tag>
                </div>
              </Space>
            </Card>

            {selectedQueries.length === 0 && !loadingQueriesRegions && (
              <Alert
                message="Выберите хотя бы один запрос для запуска парсинга"
                type="warning"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
            {selectedRegionCodes.length === 0 && regionsToShow.length > 0 && !loadingQueriesRegions && (
              <Alert
                message="Регион не выбран — будет использован «Вся Россия» (225)"
                type="info"
                showIcon
                style={{ marginTop: 16 }}
              />
            )}
          </>
        )}
      </Space>
    )
  }

  // Project selector mode (no project provided)
  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onStartParsing}
    >
      <Title level={4} style={{ marginBottom: 16 }}>
        Парсинг Yandex
      </Title>

      <Alert
        message="Выберите проект для запуска парсинга"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form.Item
        name="project_id"
        label="Проект"
        rules={[{ required: true, message: 'Выберите проект' }]}
      >
        <Select
          placeholder="Выберите проект"
          loading={loadingProjects}
          showSearch
          optionFilterProp="children"
          options={projects.map(p => ({
            value: p.id,
            label: `${p.project_name} (CRM: ${p.crm_id})`,
          }))}
          onChange={(value) => {
            if (value) {
              onLoadQueriesRegions(value)
            }
          }}
        />
      </Form.Item>
    </Form>
  )
}
